import { ref } from 'vue';
import requestHandler from '@/composables/requestHandler';

export default function useSkillProficiencyLevels() {
  const proficiencyLevels = ref([]);
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * Busca os níveis de proficiência específicos para habilidades
   * @returns {Promise<Array>} Array com os níveis de proficiência
   */
  async function fetchSkillProficiencyLevels() {
    try {
      isLoading.value = true;
      error.value = null;
      
      const response = await requestHandler.executeRequest(
        '/api/curriculum/skill/proficiency-levels',
        {},
        'get'
      );

      if (response.success && response.data) {
        // Mapear os dados da API para o formato esperado pelo frontend
        const mappedLevels = response.data.map(level => ({
          proficiency_id: level.proficiency_id,
          proficiency_level: level.proficiency_level,
          category: level.category,
          weight: level.weight,
          // Traduções para todos os idiomas suportados
          en: level.proficiency_level,
          pt: getPortugueseTranslation(level.proficiency_level),
          es: getSpanishTranslation(level.proficiency_level)
        }));
        
        // Remover duplicatas baseado no proficiency_id E no nome traduzido
        const unique = [];
        mappedLevels.forEach(lvl => {
          if (!unique.some(u => u.proficiency_id === lvl.proficiency_id || u.pt === lvl.pt)) {
            unique.push(lvl);
          }
        });
        proficiencyLevels.value = unique;
        return proficiencyLevels.value;
      } else {
        throw new Error('Resposta inválida da API');
      }
    } catch (err) {
      error.value = err.message;
      
      // Fallback para dados locais em caso de erro
      proficiencyLevels.value = getFallbackLevels();
      return proficiencyLevels.value;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Obtém tradução em português para o nível de proficiência
   * @param {string} level - Nível em inglês
   * @returns {string} Tradução em português
   */
  function getPortugueseTranslation(level) {
    const translations = {
      'beginner': 'Iniciante',
      'advanced beginner': 'Iniciante Avançado',
      'intermediate': 'Intermediário',
      'advanced': 'Avançado',
      'expert': 'Especialista'
    };
    return translations[level] || level;
  }

  /**
   * Obtém tradução em espanhol para o nível de proficiência
   * @param {string} level - Nível em inglês
   * @returns {string} Tradução em espanhol
   */
  function getSpanishTranslation(level) {
    const translations = {
      'beginner': 'Principiante',
      'advanced beginner': 'Principiante Avanzado',
      'intermediate': 'Intermedio',
      'advanced': 'Avanzado',
      'expert': 'Experto'
    };
    return translations[level] || level;
  }

  /**
   * Dados de fallback em caso de erro na API
   * @returns {Array} Array com níveis de proficiência padrão
   */
  function getFallbackLevels() {
    return [
      {
        proficiency_id: 1,
        proficiency_level: "beginner",
        category: "level",
        weight: 15.00,
        en: "beginner",
        pt: "Iniciante",
        es: "Principiante"
      },
      {
        proficiency_id: 2,
        proficiency_level: "advanced beginner",
        category: "level",
        weight: 35.00,
        en: "advanced beginner",
        pt: "Iniciante Avançado",
        es: "Principiante Avanzado"
      },
      {
        proficiency_id: 3,
        proficiency_level: "intermediate",
        category: "level",
        weight: 50.00,
        en: "intermediate",
        pt: "Intermediário",
        es: "Intermedio"
      },
      {
        proficiency_id: 4,
        proficiency_level: "advanced",
        category: "level",
        weight: 75.00,
        en: "advanced",
        pt: "Avançado",
        es: "Avanzado"
      },
      {
        proficiency_id: 5,
        proficiency_level: "expert",
        category: "level",
        weight: 100.00,
        en: "expert",
        pt: "Especialista",
        es: "Experto"
      }
    ];
  }

  /**
   * Obtém um nível de proficiência por ID
   * @param {number} id - ID do nível de proficiência
   * @returns {Object|null} Objeto do nível ou null se não encontrado
   */
  function getProficiencyLevelById(id) {
    return proficiencyLevels.value.find(level => level.proficiency_id === id) || null;
  }

  /**
   * Obtém um nível de proficiência por nome
   * @param {string} levelName - Nome do nível (beginner, intermediate, etc.)
   * @returns {Object|null} Objeto do nível ou null se não encontrado
   */
  function getProficiencyLevelByName(levelName) {
    return proficiencyLevels.value.find(level => level.proficiency_level === levelName) || null;
  }

  /**
   * Obtém o nome traduzido do nível de proficiência
   * @param {number} id - ID do nível de proficiência
   * @param {string} langIso - Idioma desejado (pt, en, es)
   * @returns {string} Nome traduzido do nível
   */
  function getProficiencyLevelName(id, langIso = 'pt') {
    const level = getProficiencyLevelById(id);
    if (!level) return 'N/A';
    
    return level[langIso] || level.proficiency_level;
  }

  return {
    proficiencyLevels,
    isLoading,
    error,
    fetchSkillProficiencyLevels,
    getProficiencyLevelById,
    getProficiencyLevelByName,
    getProficiencyLevelName
  };
}
